import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Center,
  Divider,
  Fade,
  Flex,
  Icon,
  Input,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  SkeletonCircle,
  SkeletonText,
  Text,
  theme,
  Link
} from "@chakra-ui/react";
// import { Bolt } from "components/icons/Bolt";
import { Page } from "components/page";
import ProtectedRoute from "components/protected-route";
import Tweets from "components/tweets";
import { useSession } from "next-auth/react";
import { useRouter } from "next/dist/client/router";
import { ChangeEventHandler, useEffect, useState } from "react";
import { FaYoutube } from "react-icons/fa";
import { HiOutlineLink } from "react-icons/hi";
import { getAccount } from "../utils/sessionHelper";
import { getToken } from "../firebaseClient";
import toast from "react-hot-toast";
import { textStyle } from "theme/names";
import { UpgradePopup } from "components/upgrade-popup";
import { getPlan } from "controllers/subscription";

interface ITweet {
  fullText: string;
  full_text: string;
  user: any;
}

interface IResponse {
  data: {
    tweets: ITweet[];
  };
}

interface IResult {
  success: boolean;
  post: string;
  error?: string;
}

interface IParams {
  urlType: string;
  urlString: string;
  account: any;
}

enum UrlType {
  Article = "article",
  Youtube = "youtube",
  // Linkedin = "linkedin",
}

const getAppName = (url: string): string | null => {
  try {
    // if it does not start with https:// or http://, add it
    if (!url.startsWith("https://") && !url.startsWith("http://")) {
      url = "https://" + url;
    }

    const hostname = new URL(url).hostname;
    const appName = hostname?.replace("www.", "").split(".")[0] || null;
    return appName;
  } catch (_) {
    return null;
  }
};

const urlTypeMap: Record<
  UrlType,
  { label: string; icon: React.ReactElement; placeholder: string }
> = {
  [UrlType.Article]: {
    label: "Article",
    icon: <Icon as={HiOutlineLink} />,
    placeholder: "https://website.com",
  },
  [UrlType.Youtube]: {
    label: "Youtube Video",
    icon: <Icon as={FaYoutube} />,
    placeholder: "https://youtube.com/watch?v=...",
  },
};

export default function RepurposeContent() {
  const { data: session, status } = useSession() ?? {};
  const loading = status === "loading";
  const [isSending, setIsSending] = useState(false);
  const [linkedinPosts, setLinkedinPosts] = useState<ITweet[]>([]);
  const [hasFetched, setHasfetched] = useState<boolean>(false);
  const router = useRouter();

  // user select
  const [urlType, setUrlType] = useState<UrlType>(UrlType.Article);
  const [urlString, setUrlString] = useState<string>("");

  const handleUrlInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const url = e.target.value;

    const currAppName = getAppName(url);

    if (currAppName === UrlType.Youtube) {
      setUrlType(UrlType.Youtube);
    } else {
      setUrlType(UrlType.Article);
    }

    setUrlString(url);
  };

  const getPosts = async (params: IParams): Promise<IResponse> => {
    const { account } = params;

    const tokenuserid = session?.user?.uid;

    if (!tokenuserid) {
      throw new Error("User is not authorised");
    }

    const token = await getToken(session);

    const res = await fetch(
      // `http://127.0.0.1:5001/ez4cast/us-central1/generateFromLink-generateLinkedinPostFromLink`,
      `https://us-central1-ez4cast.cloudfunctions.net/generateFromLink-generateLinkedinPostFromLink`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          tokenuserid,
        },
        body: JSON.stringify({
          isInfluentLeader: true,
          account,
          urlString,
        }),
      }
    );

    const result: IResult = await res.json();

    if (!result.success) {
      throw new Error(result?.error || "Formatting error");
    }

    const formatResponse = [result.post].map((tweet: string) => ({
      fullText: tweet.replace(/"/g, ""),
      full_text: tweet.replace(/"/g, ""),
      user: {
        name: account.name,
        description: account.description,
        image: account.image,
        profile_image_url_https: account.imageHosted,
        username: account.username,
      },
    }));

    let formattedResponse: IResponse = {
      data: {
        tweets: formatResponse,
      },
    };

    return formattedResponse;
  };

  useEffect(() => {
    const { from, idea } = router.query;
    if (session && !hasFetched && from && idea) {
      setHasfetched(true);
      sendRequest(session);
    }
  }, [session]);

  const sendRequest = async (session) => {
    try {
      if (isSending) return;

      setIsSending(true);

      // check if it's a linkedin post url, not a profile url or anything else
      if (urlType === UrlType.Youtube && !urlString.includes("watch?v=")) {
        throw new Error("Please ensure that you are using a video url");
      }

      const account = getAccount(session);

      if (!account) {
        throw new Error("Please add a LinkedIn account first");
        return;
      }

      const posts = await getPosts({
        urlType,
        urlString,
        account,
      });

      const newTweets = posts?.data?.tweets ? posts?.data?.tweets : [];
      setLinkedinPosts(newTweets);
    } catch (error) {
      console.error(error);
      toast.error(
        error.message ?? "Something went wrong while generating post"
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    await sendRequest(session);
  };

  if (!loading && !session) {
    return <ProtectedRoute />;
  }

  const plan = getPlan(session?.user);
  const isStarterPlan = plan === "start";
  return (
    <Page
      title="Repurpose Content Into Posts"
      description="Create posts based on any URL or YouTube video."
    >
      {isStarterPlan ? (
        <UpgradePopup
          currentPlan={plan}
          feature="Repurpose Content"
          availablePlans={["grow", "enterprise"]}
        />
      ) : (
        <>
          <Flex
            mt={6}
            gap={4}
            maxW="4xl"
            alignItems={"stretch"}
            as="form"
            onSubmit={handleSubmit}
          >
            <Box w="100%">
              <Box
                display="flex"
                alignItems="flex-start"
                border="1px solid"
                borderColor="transparent"
                borderRadius={"md"}
                bg="gray.100"
                _focusWithin={{
                  boxShadow: "md",
                  borderColor: "inherit",
                }}
              >
                <Menu>
                  <MenuButton
                    borderRightRadius={"0"}
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    textTransform={"capitalize"}
                    w="fit-content"
                    maxW="fit-content"
                    minW={"fit-content"}
                    fontSize={"sm"}
                    borderLeftRadius={"md"}
                    variant={"secondary"}
                    leftIcon={urlTypeMap[urlType].icon}
                  >
                    <Text minW="fit-content">{urlTypeMap[urlType].label}</Text>
                  </MenuButton>
                  <MenuList border="none">
                    <MenuOptionGroup
                      defaultValue={UrlType.Article}
                      value={urlType}
                      onChange={(value) => setUrlType(value as UrlType)}
                      type="radio"
                    >
                      {Object.keys(urlTypeMap).map((type) => (
                        <MenuItemOption key={type} value={type}>
                          {urlTypeMap[type].label}
                        </MenuItemOption>
                      ))}
                    </MenuOptionGroup>
                  </MenuList>
                </Menu>
                <Center h="100%" py={1}>
                  <Divider orientation="vertical" borderColor={"gray.400"} />
                </Center>
                <Input
                  border="none"
                  _hover={{ border: "none" }}
                  _focus={{ border: "none" }}
                  borderLeftRadius={"0"}
                  borderLeft={"0"}
                  variant="filled"
                  onChange={handleUrlInputChange}
                  required
                  value={urlString}
                  autoFocus
                  fontSize={"sm"}
                  placeholder={urlTypeMap[urlType].placeholder}
                />
              </Box>

              {urlType === UrlType.Youtube && (
                <Text
                  fontSize={"sm"}
                  align={"center"}
                  mt={2}
                  fontWeight={"regular"}
                  color={theme.colors.gray[500]}
                  px="12"
                >
                  We strongly recommended using videos that are voice-intensive and
                  less than 15 minutes long.
                </Text>
              )}
            </Box>

            <Button variant={"primaryAI"} px={4} type="submit" minW="36">
              ⚡️ Generate
            </Button>
          </Flex>

          <Fade in={isSending} unmountOnExit>
            <Box py="24" maxW="2xl" px="8">
              <Text fontSize={"sm"} align={"center"}>
                This may take a minute as we extract information from the URL you
                provided. Feel free to come back to this page in a few moments, your
                content will be waiting for you.
              </Text>
            </Box>
          </Fade>

          <Box pt={10}>
            {isSending ? (
              <Flex gap="5">
                {Array.from({ length: 1 }, (_, index) => index + 1).map(
                  (item, index) => (
                    <Box
                      key={"postLoading" + index}
                      h="326"
                      width={"300px"}
                      boxShadow="lg"
                      bg="white"
                      borderRadius={"10px"}
                    >
                      <Box padding={4}>
                        <SkeletonCircle size="5" />
                      </Box>
                      <Divider />
                      <Box padding={4}>
                        <SkeletonText
                          mt="4"
                          noOfLines={9}
                          spacing="4"
                          skeletonHeight="2"
                        />
                      </Box>
                    </Box>
                  )
                )}
              </Flex>
            ) : (
              linkedinPosts && (
                <Box w="100%">
                  <Tweets
                    tweets={linkedinPosts}
                    disableBlur={true}
                    disableBookmarkButton={true}
                    disableSaveCsv={true}
                    disableEmbed={true}
                    disableTryAgain={true}
                    disableHideButton={true}
                    disableRequest={true}
                    disableSubMenu={true}
                    disableName={false}
                    isVariation={true}
                    disableAddToQueue={false}
                    disableTweetSelection={true}
                    type="linkedin"
                    masonryContainerDisplay="block"
                  />
                </Box>
              )
            )}
          </Box>
        </>
      )}
    </Page>
  );
}
